import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';
import type { CompensationRule } from '@/services/compensation/types';

interface ConflictDetectionCardProps {
  employeeRules: CompensationRule[];
  newRule?: CompensationRule;
  onResolveConflict?: (conflictId: string) => void;
}

interface RuleConflict {
  id: string;
  type: 'overlap' | 'duplicate' | 'priority' | 'date_range';
  severity: 'high' | 'medium' | 'low';
  message: string;
  affectedRules: CompensationRule[];
  suggestion?: string;
}

export function ConflictDetectionCard({
  employeeRules,
  newRule,
  onResolveConflict,
}: ConflictDetectionCardProps) {
  
  const detectConflicts = (): RuleConflict[] => {
    const conflicts: RuleConflict[] = [];
    const allRules = newRule ? [...employeeRules, newRule] : employeeRules;

    // Check for duplicate rule types
    const typeGroups = allRules.reduce((acc, rule) => {
      if (!acc[rule.rule_type]) acc[rule.rule_type] = [];
      acc[rule.rule_type].push(rule);
      return acc;
    }, {} as Record<string, CompensationRule[]>);

    Object.entries(typeGroups).forEach(([type, rules]) => {
      if (rules.length > 1 && type !== 'allowance' && type !== 'deduction') {
        conflicts.push({
          id: `duplicate_${type}`,
          type: 'duplicate',
          severity: 'high',
          message: `Multiple ${type.replace('_', ' ')} rules detected`,
          affectedRules: rules,
          suggestion: 'Consider consolidating into a single rule or using different effective dates'
        });
      }
    });

    // Check for date range overlaps
    allRules.forEach((rule1, i) => {
      allRules.slice(i + 1).forEach(rule2 => {
        if (rule1.rule_type === rule2.rule_type) {
          const rule1Start = new Date(rule1.effective_from);
          const rule1End = rule1.effective_to ? new Date(rule1.effective_to) : new Date('2099-12-31');
          const rule2Start = new Date(rule2.effective_from);
          const rule2End = rule2.effective_to ? new Date(rule2.effective_to) : new Date('2099-12-31');

          if (rule1Start <= rule2End && rule2Start <= rule1End) {
            conflicts.push({
              id: `overlap_${rule1.id}_${rule2.id}`,
              type: 'overlap',
              severity: 'medium',
              message: `Date range overlap between "${rule1.rule_name}" and "${rule2.rule_name}"`,
              affectedRules: [rule1, rule2],
              suggestion: 'Adjust effective dates to prevent overlapping periods'
            });
          }
        }
      });
    });

    // Check for priority conflicts
    const activeSameTypeRules = allRules.filter(rule => rule.is_active);
    const priorityGroups = activeSameTypeRules.reduce((acc, rule) => {
      const key = `${rule.rule_type}_${rule.priority_order || 1}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(rule);
      return acc;
    }, {} as Record<string, CompensationRule[]>);

    Object.entries(priorityGroups).forEach(([key, rules]) => {
      if (rules.length > 1) {
        conflicts.push({
          id: `priority_${key}`,
          type: 'priority',
          severity: 'low',
          message: `Multiple rules with same priority level`,
          affectedRules: rules,
          suggestion: 'Assign different priority orders for proper rule execution'
        });
      }
    });

    return conflicts;
  };

  const conflicts = detectConflicts();

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'medium':
        return <Info className="h-4 w-4 text-warning" />;
      case 'low':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const getSeverityVariant = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  if (conflicts.length === 0) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-green-700">
            <CheckCircle className="h-5 w-5" />
            No Conflicts Detected
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-green-600">
            All compensation rules are compatible and no conflicts were found.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-orange-200 bg-orange-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-700">
          <AlertTriangle className="h-5 w-5" />
          Rule Conflicts Detected
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {conflicts.map((conflict) => (
          <div key={conflict.id} className="border rounded-lg p-3 bg-background">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                {getSeverityIcon(conflict.severity)}
                <span className="font-medium text-sm">{conflict.message}</span>
                <Badge variant={getSeverityVariant(conflict.severity)} className="text-xs">
                  {conflict.severity.toUpperCase()}
                </Badge>
              </div>
              {onResolveConflict && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onResolveConflict(conflict.id)}
                >
                  Resolve
                </Button>
              )}
            </div>
            
            <div className="ml-6 space-y-2">
              <div className="text-xs text-muted-foreground">
                Affected Rules: {conflict.affectedRules.map(rule => rule.rule_name).join(', ')}
              </div>
              
              {conflict.suggestion && (
                <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
                  <strong>Suggestion:</strong> {conflict.suggestion}
                </div>
              )}
            </div>
          </div>
        ))}
        
        <div className="mt-4 p-3 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground">
            <strong>Note:</strong> Conflicts may affect payroll calculations and should be resolved before finalizing assignments.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}